import React from "react";
import styled from "styled-components";
import { Card, Empty, Space, Tag, Typography } from "antd";
import {
  formatPickupTime,
} from "../../../shared/utils/formatPickupTime.js";

const { Text } = Typography;

const BoardCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const Column = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px;
  min-height: 160px;
`;

const ColumnHeader = styled(Space)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const TripCard = styled.a`
  display: block;
  border: 1px solid ${({ $severity }) => ($severity === "warning" ? "#ffe58f" : "#e8e8e8")};
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
  background: ${({ $severity }) => ($severity === "warning" ? "#fffbe6" : "#ffffff")};
  color: inherit;

  &:hover {
    color: inherit;
    border-color: #005249;
  }
`;

const LiveTripBoardWidget = ({ board }) => {
  if (!hasBoardCards(board)) {
    return (
      <BoardCard title="Live Trip Board">
        <Empty
          description="No live trip board records for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </BoardCard>
    );
  }

  return (
    <BoardCard title="Live Trip Board" data-testid="live-trip-board-widget">
      <BoardGrid>
        {board.columns.map((column) => (
          <Column key={column.key}>
            <ColumnHeader>
              <Text strong>{column.label}</Text>
              <Tag color={column.cards.length > 0 ? "blue" : "default"}>
                {column.cards.length}
              </Tag>
            </ColumnHeader>
            {column.cards.length > 0 ? (
              column.cards.map((card) => <BoardItem key={card.id} card={card} />)
            ) : (
              <Text type="secondary">No records</Text>
            )}
          </Column>
        ))}
      </BoardGrid>
    </BoardCard>
  );
};

const BoardItem = ({ card }) => (
  <TripCard
    href={card.detailPath || undefined}
    $severity={card.severity}
    data-board-column={card.columnKey}
  >
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      <Space size={6} wrap>
        <Text strong>{card.title}</Text>
        {card.severity === "warning" && <Tag color="orange">Delayed</Tag>}
      </Space>
      {card.customer && <Text>{card.customer}</Text>}
      <Text type="secondary">{card.route}</Text>
      <Space size={[6, 6]} wrap>
        <Tag>{formatStatus(card.status)}</Tag>
        {card.vehicle && <Tag>Vehicle: {card.vehicle}</Tag>}
        {card.driver && <Tag>Driver: {card.driver}</Tag>}
      </Space>
      {formatBoardSchedule(card) && (
        <Text type="secondary">{formatBoardSchedule(card)}</Text>
      )}
    </Space>
  </TripCard>
);

function hasBoardCards(board) {
  return board?.columns?.some((column) => column.cards?.length > 0);
}

function formatStatus(status) {
  return String(status || "unknown").replace(/_/g, " ");
}

function formatBoardSchedule(card) {
  if (card.entityType === "booking") {
    const pickupTime = formatPickupTime({
      moveDate: card.moveDate,
      pickupTimeWindow: card.pickupTimeWindow,
    });
    return pickupTime ? `Pickup: ${pickupTime}` : null;
  }

  if (!card.moveDate) return null;

  const departure = new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(card.moveDate));

  return `Departure: ${departure}`;
}

export default LiveTripBoardWidget;
